import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, ArrowUp, Lock } from 'lucide-react';
import InfoAndMap from './InfoAndMap';
import MediaImage from './MediaImage';
import SiteNews from './SiteNews';
import SitePromoBanner from './SitePromoBanner';
import SocialGridSection from './SocialGridSection';
import SmoothScroll from './SmoothScroll';
import { useSiteContent } from '../content/SiteContentProvider';
import { getText } from '../content/cmsAccess';
import { navigationImages } from '../content/brandAssets';
import DiagonalFormulaImage from './DiagonalFormulaImage';

export default function Layout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isBookingMenuOpen, setIsBookingMenuOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const bookingMenuRef = React.useRef<HTMLDivElement>(null);
  const mobileBookingMenuRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { content } = useSiteContent();
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const isContactPage = normalizedPathname === '/contact';
  const navIsSolid = isScrolled || isContactPage;

  const ACTIVITES = [
    { title: 'Action Game', image: getText(content.pages.actionGame.intro, 'image', content.images.actionGame.intro), link: '/nos-activites/action-game' },
    { title: 'Karaoké', image: getText(content.pages.karaoke.card, 'image'), link: '/nos-activites/karaoke' },
    { title: 'Nouvelle Activité', image: '', link: '/nos-activites/nouvelle-activite', isComingSoon: true, subtitle: 'Prochainement' },
  ];

  const ENTREPRISES = [
    { title: 'Team Building', image: navigationImages.entreprises.teamBuilding, link: '/entreprises/team-building' },
    { title: 'Privatisation', image: navigationImages.entreprises.privatisation, link: '/entreprises/privatisation' },
    { title: 'Séminaire', image: navigationImages.entreprises.seminaire, link: '/entreprises/seminaire' },
  ];

  const FORMULES = [
    { title: 'EVG / EVJF', image: navigationImages.formules.evg, secondaryImage: navigationImages.formules.evjf, link: '/formules/evg-evjf' },
    { title: 'Anniversaire Enfant', image: navigationImages.formules.anniversaireEnfant, link: '/formules/anniversaire-enfant' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);

      // Hide navbar when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const closeBookingMenu = (event: MouseEvent) => {
      if (!bookingMenuRef.current?.contains(event.target as Node)
        && !mobileBookingMenuRef.current?.contains(event.target as Node)) {
        setIsBookingMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsBookingMenuOpen(false);
    };
    document.addEventListener('mousedown', closeBookingMenu);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeBookingMenu);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsBookingMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white font-sans antialiased selection:bg-yellow-400 selection:text-white relative flex flex-col">
      <SmoothScroll />
      <header className={`fixed top-0 left-0 w-full z-50 flex flex-col transition-all duration-300 ease-in-out ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}>
        {!isContactPage && <SitePromoBanner />}
        <nav className={`w-full px-4 lg:px-12 flex justify-between items-center transition-all duration-300 ease-in-out ${navIsSolid ? 'bg-[#14151f] backdrop-blur-md py-3 shadow-[0_4px_30px_rgba(0,0,0,0.3)] rounded-b-3xl' : 'bg-transparent py-4 lg:py-6'}`}>
          <div className="flex items-center shrink-0 relative z-[60]">
            <Link to="/" className="relative flex items-center">
              <MediaImage
                src={content.brand.logos.primary}
                alt="Gameside Caen logo"
                width={299}
                height={81}
                className={`w-auto max-w-[calc(100vw-7rem)] object-contain transition-all duration-300 ${navIsSolid ? 'h-11 lg:h-14' : 'h-12 sm:h-14 lg:h-16'}`}
                priority
                sizes="(min-width: 1024px) 12rem, 10rem"
              />
            </Link>
          </div>

          <div className="hidden xl:flex items-center space-x-8">
            <MegaDropdown text="NOS ACTIVITÉS" items={ACTIVITES} />
            <MegaDropdown text="ENTREPRISES" items={ENTREPRISES} />
            <MegaDropdown text="NOS FORMULES" items={FORMULES} />
            <Link to="/actualites" className="text-sm font-bold text-white hover:text-yellow-400 transition-colors uppercase tracking-wide">
              ACTUALITÉS
            </Link>
            <Link to="/infos-pratiques" className="text-sm font-bold text-white hover:text-yellow-400 transition-colors uppercase tracking-wide">
              INFOS PRATIQUES
            </Link>
            
            <div className="relative ml-4" ref={bookingMenuRef}>
              <button
                type="button"
                onClick={() => setIsBookingMenuOpen((open) => !open)}
                aria-expanded={isBookingMenuOpen}
                aria-haspopup="menu"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-[#1a1b24] font-extrabold text-sm px-6 py-3 rounded-full flex items-center gap-2 transition-colors uppercase tracking-widest border-none"
              >
                JE RÉSERVE
                <ChevronDown size={16} strokeWidth={3} className={`transition-transform ${isBookingMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {isBookingMenuOpen && (
                <BookingChoiceMenu onSelect={() => setIsBookingMenuOpen(false)} onDismiss={() => setIsBookingMenuOpen(false)} />
              )}
            </div>
          </div>

          <button 
            className="xl:hidden text-white hover:text-yellow-400 transition-colors relative p-1 z-[60]"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <Menu size={36} />
          </button>
        </nav>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] bg-[#14151f] flex flex-col overflow-y-auto xl:hidden">
          <div className="flex justify-between items-center w-full px-4 lg:px-12 py-4 lg:py-6 shrink-0 border-b border-white/5">
            <Link to="/" className="flex items-center shrink-0">
              <MediaImage src={content.brand.logos.primary} alt="Gameside Caen logo mobile" width={299} height={81} className="h-10 object-contain" priority sizes="10rem" />
            </Link>
            <button className="text-white hover:text-yellow-400 transition-colors p-1" onClick={() => setIsMobileMenuOpen(false)} aria-label="Fermer le menu">
              <X size={36} />
            </button>
          </div>
          <div className="flex flex-col flex-1 px-6 lg:px-12 pt-6 pb-8 overflow-y-auto">
            <div className="flex flex-col gap-5 items-stretch text-left">
              <div className="flex flex-col gap-1 w-full">
                <span className="text-[11px] font-black text-yellow-400 uppercase tracking-[0.24em] mb-1">Nos activités</span>
                {ACTIVITES.map(item => (
                  item.isComingSoon ? (
                    <span key={item.title} className="text-base font-bold text-gray-500 uppercase tracking-wide py-2 inline-flex items-center gap-2 cursor-not-allowed">
                      <Lock size={14} strokeWidth={2.6} className="text-gray-600" />
                      {item.title}
                      <span className="ml-auto text-[10px] font-black tracking-[0.2em] text-gray-600 uppercase">Bientôt</span>
                    </span>
                  ) : (
                    <Link key={item.title} to={item.link} className="text-base font-bold text-white hover:text-yellow-400 uppercase tracking-wide py-2 transition-colors">{item.title}</Link>
                  )
                ))}
              </div>
              <div className="h-px bg-white/8" />
              <div className="flex flex-col gap-1 w-full">
                <span className="text-[11px] font-black text-yellow-400 uppercase tracking-[0.24em] mb-1">Entreprises</span>
                {ENTREPRISES.map(item => (
                  <Link key={item.title} to={item.link} className="text-base font-bold text-white hover:text-yellow-400 uppercase tracking-wide py-2 transition-colors">{item.title}</Link>
                ))}
              </div>
              <div className="h-px bg-white/8" />
              <div className="flex flex-col gap-1 w-full">
                <span className="text-[11px] font-black text-yellow-400 uppercase tracking-[0.24em] mb-1">Nos formules</span>
                {FORMULES.map(item => (
                   <Link key={item.title} to={item.link} className="text-base font-bold text-white hover:text-yellow-400 uppercase tracking-wide py-2 transition-colors">{item.title}</Link>
                ))}
              </div>
              <div className="h-px bg-white/8" />
              <div className="flex flex-col gap-1 w-full">
                <Link to="/infos-pratiques" className="text-base font-bold text-white hover:text-yellow-400 uppercase tracking-wide py-2 transition-colors">Infos pratiques</Link>
                <Link to="/actualites" className="text-base font-bold text-white hover:text-yellow-400 uppercase tracking-wide py-2 transition-colors">Actualités</Link>
              </div>
            </div>

            <div className="mt-8 shrink-0" ref={mobileBookingMenuRef}>
              <button
                type="button"
                onClick={() => setIsBookingMenuOpen((open) => !open)}
                aria-expanded={isBookingMenuOpen}
                className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-300 hover:to-yellow-400 text-[#1a1b24] font-extrabold text-sm md:text-base w-full py-4 rounded-full flex justify-center items-center gap-2 uppercase tracking-widest shadow-lg"
              >
                JE RÉSERVE <ChevronDown size={20} strokeWidth={3} className={`transition-transform ${isBookingMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {isBookingMenuOpen && (
                <BookingChoiceMenu
                  mobile
                  onDismiss={() => setIsBookingMenuOpen(false)}
                  onSelect={() => { setIsBookingMenuOpen(false); setIsMobileMenuOpen(false); }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main className="flex-grow w-full">
        <Outlet />
      </main>

      {location.pathname !== '/actualites' && <SiteNews />}
      {location.pathname === '/' && <SocialGridSection />}
      <InfoAndMap />

      {/* Footer */}
      <footer className="w-full bg-[#1b1c26] text-white pt-20 pb-10 px-6 lg:px-12 relative z-20 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.03] select-none pointer-events-none z-0 text-[15vw] font-black leading-none whitespace-nowrap">
          GAMESIDE
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-8 mb-16">
            <div className="lg:col-span-1 flex flex-col items-center lg:items-start text-center lg:text-left">
              <Link to="/" className="mb-6">
                <MediaImage src={content.brand.logos.primary} alt="Gameside Caen logo footer" width={299} height={81} className="h-16 object-contain" sizes="12rem" />
              </Link>
              <div className="text-gray-400 text-sm flex flex-col gap-2 mt-2">
                <a href={content.directionsUrl} target="_blank" rel="noreferrer" className="hover:text-white transition-colors leading-relaxed">
                  {content.addressLines[0]}<br/>
                  {content.addressLines[1]}
                </a>
                <a href={content.phoneHref} className="text-white hover:text-yellow-400 font-bold mt-2 transition-colors">{content.phoneDisplay}</a>
              </div>
              <div className="flex gap-3 mt-5">
                <a
                  href={getText(content.pages.home.social, 'instagramHref', 'https://www.instagram.com/gameside.caen/')}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Gameside Caen sur Instagram"
                  className="w-9 h-9 rounded-full bg-white/8 hover:bg-yellow-400 text-gray-400 hover:text-[#111722] flex items-center justify-center transition-all duration-200"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                    <circle cx="12" cy="12" r="4.5"/>
                    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
                  </svg>
                </a>
                <a
                  href={getText(content.pages.home.social, 'facebookHref', 'https://www.facebook.com/gameside.caen/')}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Gameside Caen sur Facebook"
                  className="w-9 h-9 rounded-full bg-white/8 hover:bg-yellow-400 text-gray-400 hover:text-[#111722] flex items-center justify-center transition-all duration-200"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                </a>
              </div>
            </div>
            <div className="lg:col-span-1">
              <h4 className="font-black mb-6 uppercase tracking-widest text-yellow-400 text-sm">NOS ACTIVITÉS</h4>
              <ul className="space-y-3 font-medium text-sm text-gray-400">
                {ACTIVITES.filter((item) => !item.isComingSoon).map(item => (
                  <li key={item.title}>
                    <Link to={item.link} className="hover:text-white transition-colors border-b border-transparent hover:border-white w-max pb-0.5 uppercase">{item.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-1">
              <h4 className="font-black mb-6 uppercase tracking-widest text-yellow-400 text-sm">ENTREPRISES</h4>
              <ul className="space-y-3 font-medium text-sm text-gray-400">
                {ENTREPRISES.map(item => (
                  <li key={item.title}><Link to={item.link} className="hover:text-white transition-colors border-b border-transparent hover:border-white w-max pb-0.5 uppercase">{item.title}</Link></li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-1">
              <h4 className="font-black mb-6 uppercase tracking-widest text-yellow-400 text-sm">NOS FORMULES</h4>
              <ul className="space-y-3 font-medium text-sm text-gray-400">
                {FORMULES.map(item => (
                  <li key={item.title}><Link to={item.link} className="hover:text-white transition-colors border-b border-transparent hover:border-white w-max pb-0.5 uppercase">{item.title}</Link></li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-1 flex flex-col gap-5 font-bold text-sm">
              <h4 className="font-black mb-1 uppercase tracking-widest text-yellow-400 text-sm">CONTACT</h4>
              <Link to="/contact" className="hover:text-yellow-400 transition-colors uppercase tracking-wide">
                Contact
              </Link>
              <Link to="/infos-pratiques" className="hover:text-yellow-400 transition-colors uppercase tracking-wide">
                Infos pratiques
              </Link>
              <Link to="/actualites" className="hover:text-yellow-400 transition-colors uppercase tracking-wide">
                Actualités
              </Link>
              <Link to="/contact" className="hover:text-yellow-400 transition-colors uppercase tracking-wide">
                Demande groupe
              </Link>
              <a
                href="https://www.gameside.fr/ouvrir-votre-gameside/"
                target="_blank"
                rel="noreferrer noopener"
                className="hover:text-yellow-400 transition-colors uppercase tracking-wide"
              >
                Devenir franchisé
              </a>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 mt-8 flex flex-col md:flex-row justify-between items-center gap-6 text-center text-[11px] font-medium text-gray-400 tracking-wide">
            <div>
              ©{new Date().getFullYear()} GAMESIDE Tous droits réservés
            </div>
            <div className="flex flex-wrap justify-center md:justify-end gap-4 lg:gap-8">
                <Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link>
                <Link to="/politique-de-confidentialite" className="hover:text-white transition-colors">Politique de confidentialité</Link>
                <Link to="/politique-de-cookies-ue" className="hover:text-white transition-colors">Politique de cookies (UE)</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Action Button */}
      <button 
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-5 right-4 sm:bottom-6 sm:right-6 lg:bottom-8 lg:right-8 w-10 h-10 sm:w-11 sm:h-11 lg:w-12 lg:h-12 bg-yellow-400 hover:bg-yellow-400 text-white rounded-full flex items-center justify-center shadow-lg hover:-translate-y-1 transition-all duration-300 z-50 group"
        aria-label="Back to top"
      >
        <ArrowUp size={20} strokeWidth={2.5} className="group-hover:-translate-y-1 transition-transform duration-300 sm:w-[22px] sm:h-[22px] lg:w-6 lg:h-6" />
      </button>
    </div>
  );
}

function BookingChoiceMenu({ onSelect, onDismiss, mobile = false }: { onSelect: () => void; onDismiss: () => void; mobile?: boolean }) {
  const { content } = useSiteContent();
  const choices = [
    {
      title: 'Action Game',
      href: '/nos-activites/action-game#reservation',
      image: getText(content.pages.actionGame.intro, 'image', content.images.actionGame.intro),
    },
    {
      title: 'Karaoké',
      href: '/nos-activites/karaoke#reservation',
      image: getText(content.pages.karaoke.card, 'image'),
    },
  ];

  return (
    <div
      role="menu"
      aria-label="Choisir une activité à réserver"
      className={mobile
        ? 'fixed inset-x-4 bottom-4 z-[120] rounded-3xl border border-white/10 bg-[#20212b] p-3 shadow-[0_24px_80px_rgba(0,0,0,0.6)]'
        : 'absolute right-0 top-[calc(100%+14px)] w-[560px] rounded-[30px] bg-white p-3 shadow-[0_24px_70px_rgba(0,0,0,0.34)] before:absolute before:-top-4 before:right-8 before:h-4 before:w-28 before:content-[\"\"]'}
    >
      {mobile && (
        <button type="button" onClick={onDismiss} className="absolute right-2 top-2 z-20 rounded-full bg-black/55 p-1.5 text-white/70 backdrop-blur-sm hover:text-white" aria-label="Fermer le choix de réservation">
          <X size={18} />
        </button>
      )}
      <div className="grid grid-cols-2 gap-3">
        {choices.map(({ title, href, image }) => (
          <Link
            key={title}
            to={href}
            role="menuitem"
            onClick={onSelect}
            className={`group relative block overflow-hidden bg-[#14151f] ${mobile ? 'aspect-[4/3] rounded-2xl' : 'aspect-[2/1] rounded-[1.5rem]'}`}
          >
            <MediaImage src={image} alt={title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" sizes={mobile ? '45vw' : '260px'} />
            <div className="absolute inset-0 bg-black/45 transition-colors group-hover:bg-black/30" />
            <span className="absolute inset-0 flex items-center justify-center p-3 text-center text-sm font-black uppercase tracking-wider text-white drop-shadow-md transition-transform duration-300 group-hover:-translate-y-1 sm:text-base">
              {title}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function MegaDropdown({ text, items }: { text: string, items: { title: string, image: string, secondaryImage?: string, link: string, isComingSoon?: boolean, subtitle?: string }[] }) {
  // Fix position for the rightmost menu element to avoid overflowing.
  const alignmentClass = text === "NOS FORMULES" ? "right-1/2 translate-x-[40%]" : "left-1/2 -translate-x-1/2";
  const gridClass = items.length === 3 ? 'grid-cols-3' : 'grid-cols-2';
  const showFullImage = text === 'NOS FORMULES';

  return (
    <div className="group relative cursor-pointer h-full flex items-center px-1">
      <div className="flex items-center gap-1.5 text-sm font-bold text-white group-hover:text-yellow-400 transition-colors uppercase tracking-wide py-6 -my-6">
        {text}
        <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300" />
      </div>
      
      {/* Dropdown Container */}
      <div className={`absolute top-[100%] ${alignmentClass} opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 z-50 pt-4`}>
        {/* Invisible bridge for hover */}
        <div className="absolute top-0 left-0 w-full h-8 bg-transparent"></div>
        <div className={`bg-white rounded-[2rem] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.5)] p-3 grid ${gridClass} gap-3 w-max relative`}>
          {items.map((item, i) => {
            const Content = () => (
              <>
                 {item.image && (
                   <>
                     {showFullImage && (
                       item.secondaryImage ? (
                         <DiagonalFormulaImage leftImage={item.image} rightImage={item.secondaryImage} alt="" className="scale-110 blur-lg opacity-80" sizes="260px" />
                       ) : (
                         <MediaImage src={item.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg opacity-80" sizes="260px" />
                       )
                     )}
                     {item.secondaryImage ? (
                       <DiagonalFormulaImage
                         leftImage={item.image}
                         rightImage={item.secondaryImage}
                         alt={`${item.title} Gameside Caen`}
                         className="transform transition-transform duration-500 group-hover/item:scale-[1.02]"
                         sizes="260px"
                       />
                     ) : (
                       <MediaImage
                         src={item.image}
                         alt={`${item.title} Gameside Caen`}
                         className={`absolute inset-0 w-full h-full transform transition-transform duration-500 group-hover/item:scale-[1.02] ${showFullImage ? 'object-contain' : 'object-cover'}`}
                         sizes="260px"
                       />
                     )}
                   </>
                 )}
                 <div className={`absolute inset-0 transition-colors duration-300 ${item.isComingSoon ? 'bg-zinc-900' : 'bg-black/45 group-hover/item:bg-black/30'}`}></div>
                 <div className="absolute inset-0 flex flex-col items-center justify-center p-3 text-center">
                    <span className={`text-white font-black text-sm lg:text-[17px] uppercase tracking-wider drop-shadow-md leading-tight transition-transform duration-300 ${item.isComingSoon ? '' : 'group-hover/item:-translate-y-1'}`}>
                      {item.title}
                    </span>
                    {item.subtitle && (
                      <span className="text-gray-200 text-[10px] font-bold uppercase tracking-widest mt-1 bg-black/80 px-2.5 py-1 rounded shadow-sm">
                        {item.subtitle}
                      </span>
                    )}
                 </div>
              </>
            );

            return item.isComingSoon ? (
              <div key={i} className="flex-none relative rounded-[1.5rem] overflow-hidden w-[190px] lg:w-[260px] aspect-[2/1] group/item isolation-auto block cursor-not-allowed bg-zinc-800">
                <Content />
              </div>
            ) : (
              <Link key={i} to={item.link} className="flex-none relative rounded-[1.5rem] overflow-hidden w-[190px] lg:w-[260px] aspect-[2/1] group/item isolation-auto block bg-[#1a1b24]">
                <Content />
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
